import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Image,
  Video,
  FileText,
  File,
} from 'lucide-react';
import {
  format,
  formatISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  subMonths,
  addMonths,
  subDays,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { sq } from 'date-fns/locale';
import clsx from 'clsx';
import { PostType } from '@barrikadave/shared';
import type { IPost, IPostFilters } from '@barrikadave/shared';
import { postsApi } from '../services/api';
import Badge, { getPriorityBadgeVariant } from '../components/Badge';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';

type DateSegment = '1' | '7' | '30' | 'custom';

const SEGMENTS: { value: DateSegment; label: string }[] = [
  { value: '1', label: '1 dite' },
  { value: '7', label: '7 dite' },
  { value: '30', label: '30 dite' },
  { value: 'custom', label: 'Personalizuar' },
];

const DAY_NAMES = ['Hen', 'Mar', 'Mer', 'Enj', 'Pre', 'Sht', 'Die'];

function getTypeIcon(type: string) {
  switch (type) {
    case PostType.PHOTO:
      return Image;
    case PostType.VIDEO:
      return Video;
    case PostType.DOCUMENT:
      return FileText;
    case PostType.MIXED:
      return File;
    default:
      return FileText;
  }
}

function getTypeColor(type: string) {
  switch (type) {
    case PostType.PHOTO:
      return 'text-blue-500';
    case PostType.VIDEO:
      return 'text-purple-500';
    case PostType.DOCUMENT:
      return 'text-amber-500';
    case PostType.MIXED:
      return 'text-emerald-500';
    default:
      return 'text-gray-500';
  }
}

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [search, setSearch] = useState('');
  const [dateSegment, setDateSegment] = useState<DateSegment>('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  const { startDate, endDate } = useMemo(() => {
    const today = new Date();
    switch (dateSegment) {
      case '1':
        return { startDate: startOfDay(today), endDate: endOfDay(today) };
      case '7':
        return { startDate: subDays(startOfDay(today), 6), endDate: endOfDay(today) };
      case '30':
        return { startDate: startOfMonth(currentMonth), endDate: endOfMonth(currentMonth) };
      case 'custom':
        return {
          startDate: customStart ? new Date(customStart) : startOfMonth(currentMonth),
          endDate: customEnd ? new Date(customEnd) : endOfMonth(currentMonth),
        };
    }
  }, [dateSegment, currentMonth, customStart, customEnd]);

  const filters: IPostFilters = {
    sort: 'latest',
    limit: 500,
    startDate: formatISO(startDate, { representation: 'date' }),
    endDate: formatISO(endDate, { representation: 'date' }),
    ...(search && { search }),
  };

  const postsQuery = useQuery({
    queryKey: ['posts', 'calendar', filters],
    queryFn: () => postsApi.getAll(filters),
  });

  const posts = postsQuery.data?.data ?? [];

  const postsByDay = useMemo(() => {
    const map = new Map<string, IPost[]>();
    for (const post of posts) {
      const key = formatISO(new Date(post.publishedAt), { representation: 'date' });
      const existing = map.get(key) ?? [];
      existing.push(post);
      map.set(key, existing);
    }
    return map;
  }, [posts]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [currentMonth]);

  const selectedDayPosts = useMemo(() => {
    if (!selectedDay) return [];
    const key = formatISO(selectedDay, { representation: 'date' });
    return postsByDay.get(key) ?? [];
  }, [selectedDay, postsByDay]);

  function handlePrevMonth() {
    setCurrentMonth((m) => subMonths(m, 1));
    setDateSegment('30');
  }

  function handleNextMonth() {
    setCurrentMonth((m) => addMonths(m, 1));
    setDateSegment('30');
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <h1 className="text-2xl font-bold text-gray-900">Kalendari i Postimeve</h1>

      {/* Filter Bar */}
      <div className="card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Kerko postime..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-9"
            />
          </div>

          {/* Date segments */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {SEGMENTS.map((seg) => (
              <button
                key={seg.value}
                onClick={() => setDateSegment(seg.value)}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  dateSegment === seg.value
                    ? 'bg-white text-barrikadave-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700',
                )}
              >
                {seg.label}
              </button>
            ))}
          </div>

          {/* Custom date pickers */}
          {dateSegment === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="input-field text-sm"
              />
              <span className="text-gray-400 text-sm">deri</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="input-field text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevMonth}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h3 className="text-lg font-semibold text-gray-900 capitalize">
          {format(currentMonth, 'LLLL yyyy', { locale: sq })}
        </h3>
        <button
          onClick={handleNextMonth}
          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      {postsQuery.isLoading ? (
        <LoadingSpinner className="py-16" />
      ) : postsQuery.isError ? (
        <div className="card text-center py-16">
          <p className="text-red-500 text-sm">Ndodhi nje gabim gjate ngarkimit.</p>
          <button onClick={() => postsQuery.refetch()} className="btn-secondary mt-3 text-sm">
            Provo perseri
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Day name headers */}
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
            {DAY_NAMES.map((name) => (
              <div key={name} className="px-2 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                {name}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {calendarDays.map((day) => {
              const key = formatISO(day, { representation: 'date' });
              const dayPosts = postsByDay.get(key) ?? [];
              const inMonth = isSameMonth(day, currentMonth);
              const todayFlag = isToday(day);
              const hasUrgent = dayPosts.some((p) => p.priority === 'URGENT');
              const hasImportant = dayPosts.some((p) => p.priority === 'IMPORTANT');

              return (
                <button
                  key={key}
                  onClick={() => dayPosts.length > 0 && setSelectedDay(day)}
                  className={clsx(
                    'min-h-[90px] p-2 border-b border-r border-gray-100 text-left transition-colors',
                    inMonth ? 'bg-white' : 'bg-gray-50/50',
                    dayPosts.length > 0 && 'hover:bg-barrikadave-50 cursor-pointer',
                    dayPosts.length === 0 && 'cursor-default',
                  )}
                >
                  <span
                    className={clsx(
                      'inline-flex items-center justify-center w-7 h-7 rounded-full text-sm',
                      todayFlag && 'bg-barrikadave-500 text-white font-bold',
                      !todayFlag && inMonth && 'text-gray-900',
                      !todayFlag && !inMonth && 'text-gray-300',
                    )}
                  >
                    {format(day, 'd')}
                  </span>

                  {dayPosts.length > 0 && (
                    <div className="mt-1">
                      <span className="text-xs font-medium text-gray-600">
                        {dayPosts.length} {dayPosts.length === 1 ? 'postim' : 'postime'}
                      </span>
                      <div className="flex gap-1 mt-0.5">
                        {hasUrgent && <span className="w-2 h-2 rounded-full bg-red-500" />}
                        {hasImportant && <span className="w-2 h-2 rounded-full bg-amber-500" />}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Total count */}
      {!postsQuery.isLoading && !postsQuery.isError && (
        <p className="text-xs text-gray-400 text-center">
          Gjithsej {posts.length} postime ne kete periudhe
        </p>
      )}

      {/* Day Detail Modal */}
      <Modal
        isOpen={!!selectedDay}
        onClose={() => setSelectedDay(null)}
        title={selectedDay ? format(selectedDay, 'd MMMM yyyy', { locale: sq }) : ''}
        size="lg"
      >
        <div className="space-y-3">
          {selectedDayPosts.map((post) => {
            const TypeIcon = getTypeIcon(post.type);
            return (
              <div
                key={post.id}
                className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <TypeIcon className={clsx('h-5 w-5 mt-0.5 shrink-0', getTypeColor(post.type))} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {post.title || post.content?.slice(0, 80) || 'Pa titull'}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-gray-500">
                      {format(new Date(post.publishedAt), 'HH:mm', { locale: sq })}
                    </span>
                    <Badge variant={getPriorityBadgeVariant(post.priority)}>{post.priority}</Badge>
                    <span className="text-xs text-gray-400">
                      {post.author.firstName} {post.author.lastName}
                    </span>
                  </div>
                </div>
                <a
                  href="/posts"
                  className="text-gray-400 hover:text-barrikadave-500 shrink-0"
                  title="Shko te postimet"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            );
          })}
          {selectedDayPosts.length === 0 && (
            <p className="text-center text-gray-400 py-8">Nuk ka postime per kete dite</p>
          )}
        </div>
      </Modal>
    </div>
  );
}
